import { WorkerDocumentFileLink, WorkerDocumentImage } from '../WorkerDocumentFile'
import {ComplianceRecommendations} from '../../../compliance/ComplianceRecommendations'
import { WorkerProfileEditor } from '../WorkerProfileEditor'
import { workerDocumentCategoryText } from '../../../../services/workerDocumentUpload'
import { WorkerDocumentUpload } from '../WorkerDocumentUpload'
import { WorkerDocumentIntelligencePanel } from '../WorkerDocumentIntelligencePanel'
import { WorkerDocumentConsentPanel } from '../WorkerDocumentConsentPanel'
import { WorkerCareerPanel } from '../WorkerCareerPanel'
import React, { useMemo, useState } from 'react'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService } from '../../../../services/worker'
import type { WorkerProfileDocument } from '../../../../types/access'
import type { BrowserPlatform } from '../../../../hooks/useBrowserPlatform'
import { WorkerSectionUnavailable } from '../WorkerRequestRecovery'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobileSecondaryButton,
  MobileSectionTitle,
  MobileSheet,
  statusTone,
} from './WorkerMobilePrimitives'

/**
 * Profile / ID — the web mirror of the Android "ID" tab.
 *
 * Document capture uses the mobile browser's own camera hint on a file input,
 * which is the only capture path that works on every target browser including
 * iOS Safari. Uploads go through the EXISTING worker document API and keep the
 * existing review lifecycle: extraction stays assistive, and nothing in the
 * browser verifies a compliance document.
 */

const documentLifecycle = (doc: WorkerProfileDocument): string => {
  if (doc.isArchived) return 'archived'
  if (doc.revokedAt) return 'revoked'
  if (doc.expiryDate) {
    const remainingMs = new Date(doc.expiryDate).getTime() - Date.now()
    if (remainingMs < 0) return 'expired'
    if (remainingMs <= 30 * 24 * 60 * 60 * 1000) return 'expiring_soon'
  }
  return String(doc.verificationStatus || doc.status || 'pending')
}

export const WorkerMobileProfile: React.FC<{
  state: WorkerWorkspaceState
  platform: BrowserPlatform
  onOpenPermissions: () => void
  onSignOut: () => void
}> = ({ state, platform, onOpenPermissions, onSignOut }) => {
  const { t, formatDate } = useI18n()
  const { data, busy, run } = state
  const profile = data.profile
  const [editing, setEditing] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState('')

  const completion = profile?.profileCompletion?.completionPercent
    ?? (profile?.profileCompletion as any)?.progress
    ?? 0

  const unreadNotifications = useMemo(
    () => data.notifications.filter((row) => !row.readAt).length,
    [data.notifications]
  )

  if (!profile) {
    return (
      <div className="wm-screen" data-testid="worker-mobile-profile">
        <MobileEmptyState
          title={t('Profile unavailable')}
          detail={t('Link an organization, or pull down to refresh your worker profile.')}
        />
      </div>
    )
  }

  return (
    <div className="wm-screen" data-testid="worker-mobile-profile">
      <MobileCard testId="worker-mobile-profile-card">
        <div className="wm-identity">
          {profile.profilePhotoUrl || (profile as any).avatarUrl ? (
            <WorkerDocumentImage source={String(profile.profilePhotoUrl || (profile as any).avatarUrl)} ownerId={profile.worker_user_id} alt="" className="wm-avatar-image" />
          ) : (
            <div className="wm-avatar" aria-hidden="true">
              {(profile.display_name || (profile as any).workerName || 'W').slice(0, 1)}
            </div>
          )}
          <div>
            <h2>{profile.display_name || (profile as any).workerName}</h2>
            <p>{profile.public_headline || t('Worker profile')}</p>
            <div
              className="wm-progress"
              role="progressbar"
              aria-valuenow={Math.max(0, Math.min(100, Number(completion) || 0))}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('Profile completion')}
            >
              <span style={{ width: `${Math.max(0, Math.min(100, Number(completion)))}%` }} />
            </div>
            <small>{t('{percent}% complete', { percent: String(completion) })}</small>
          </div>
        </div>
        <MobileSecondaryButton label={t('Edit details')} onClick={() => setEditing(true)} testId="worker-mobile-edit-profile" />
      </MobileCard>

      <WorkerCareerPanel />
      <ComplianceRecommendations self/>
      <WorkerDocumentConsentPanel profile={profile} documents={data.documents} />
      <WorkerDocumentIntelligencePanel profile={profile} documents={data.documents} onUpdated={state.load} selectedDocumentId={selectedDocumentId} />

      <MobileCard testId="worker-mobile-documents-card">
        <MobileSectionTitle
          title={t('Certifications & documents')}
          detail={t('Only an authorized reviewer can verify a document. Anything read from a photo is a suggestion for you to confirm.')}
          action={<MobileSecondaryButton label={t('Add')} disabled={Boolean(state.failures.documents) || state.recoveryCodesPending} onClick={() => setUploadOpen(true)} testId="worker-mobile-add-document" />}
        />
        {state.failures.documents ? <WorkerSectionUnavailable retry={state.load} /> : data.documents.length ? data.documents.map((doc) => (
          <div className="wm-inline-card" key={doc.documentId}>
            <div className="wm-card__heading">
              <div>
                <strong>{doc.documentName}</strong>
                <small>{t(workerDocumentCategoryText(doc))}</small>
                {doc.expiryDate ? <small>{t('Expires {date}', { date: formatDate(doc.expiryDate, { dateStyle: 'medium' }) })}</small> : null}
              </div>
              <MobileChip tone={statusTone(documentLifecycle(doc))}>
                {t(documentLifecycle(doc).replace(/_/g, ' '))}
              </MobileChip>
            </div>
            {doc.rejectionReason ? <p className="wm-inline-error">{doc.rejectionReason}</p> : null}
            <div className="wm-secondary-actions">
              {doc.fileUrl && doc.hasFile !== false ? <WorkerDocumentFileLink className="wm-button wm-button--secondary" source={doc.fileUrl} ownerId={profile.worker_user_id} /> : null}
              {doc.extractedFields && !doc.extractionConfirmedAt ? (
                <MobileSecondaryButton
                  label={t('Confirm details')}
                  disabled={Boolean(busy)}
                  onClick={() => void run(`confirm-${doc.documentId}`, () => workerService.confirmExtraction(doc.documentId, doc.extractedFields || {}), t('Details confirmed.'))}
                />
              ) : null}
              <MobileSecondaryButton
                label={t('Remove')}
                disabled={Boolean(busy) || doc.isArchived || doc.revokedAt != null}
                onClick={() => {
                  if (window.confirm(t('Remove this document?'))) {
                    void run(`remove-${doc.documentId}`, () => workerService.removeDocument(doc.documentId), t('Document removed.'))
                  }
                }}
              />
            </div>
          </div>
        )) : (
          <MobileEmptyState title={t('No documents yet')} detail={t('Add a certificate, licence, or ID so you qualify for more work.')} />
        )}
      </MobileCard>

      <MobileCard testId="worker-mobile-skills-card">
        <MobileSectionTitle
          title={t('Skills & progression')}
          detail={t('Levels, evidence, and next steps are calculated by your organization.')}
        />
        {state.failures.progression ? <WorkerSectionUnavailable retry={state.load} /> : data.progression.length ? data.progression.map((row) => (
          <div className="wm-inline-card" key={row.skillId}>
            <div className="wm-card__heading">
              <div>
                <strong>{row.skillName || t('Skill')}</strong>
                <small>{t('{hours} verified hours · {shifts} shifts', {
                  hours: String(row.completedVerifiedHours || 0),
                  shifts: String(row.completedShiftCount || 0),
                })}</small>
              </div>
              <MobileChip tone={row.isCertified ? 'positive' : 'neutral'}>
                {row.isCertified ? t('Certified') : t(String(row.currentLevel || 'in progress').replace(/_/g, ' '))}
              </MobileChip>
            </div>
            {row.claimSource === 'document_intelligence' ? <p>{t('Document-derived claim')} · {t(row.verificationStatus === 'verified' ? 'Verified skill' : 'Unverified skill')}</p> : null}
            {row.nextLevel?.label ? <p className="wm-guidance">{t('Next: {level}', { level: row.nextLevel.label })}</p> : null}
            {row.nextLevelRequirements?.map((requirement) => (
              <p className="wm-guidance" key={requirement.message}>{requirement.message}</p>
            ))}
          </div>
        )) : (
          <MobileEmptyState title={t('No skill progression yet')} detail={t('Completed shifts and verified evidence build your levels here.')} />
        )}
      </MobileCard>

      <MobileCard testId="worker-mobile-notifications-card">
        <MobileSectionTitle
          title={t('Notifications')}
          action={unreadNotifications ? (
            <MobileSecondaryButton
              label={t('Mark all read')}
              disabled={Boolean(busy)}
              onClick={() => void run('notifications-all', workerService.markAllNotificationsRead, t('All notifications marked read.'))}
            />
          ) : undefined}
        />
        {state.failures.notifications ? <WorkerSectionUnavailable retry={state.load} /> : data.notifications.length ? data.notifications.slice(0, 12).map((row) => (
          <div className="wm-inline-card" key={row.id} data-unread={row.readAt ? undefined : 'true'}>
            <strong>{row.title || t('Update')}</strong>
            <p>{row.message || row.body}</p>
            {row.actionUrl ? <a className="wm-link" href={row.actionUrl}>{t('Open related item')}</a> : null}
          </div>
        )) : (
          <MobileEmptyState title={t('No notifications')} detail={t('Shift and timesheet updates appear here.')} />
        )}
      </MobileCard>

      <MobileCard testId="worker-mobile-account-card">
        <MobileSectionTitle title={t('Account & settings')} />
        <MobileSecondaryButton label={t('App permissions')} onClick={onOpenPermissions} testId="worker-mobile-open-permissions" />
        <MobileSecondaryButton label={t('Sign out')} onClick={onSignOut} testId="worker-mobile-sign-out" />
      </MobileCard>

      <ProfileEditSheet open={editing} onClose={() => setEditing(false)} state={state} />
      <DocumentUploadSheet
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        state={state}
        platform={platform}
        onUploaded={setSelectedDocumentId}
      />
    </div>
  )
}

const ProfileEditSheet: React.FC<{ open: boolean; onClose: () => void; state: WorkerWorkspaceState }> = ({ open, onClose, state }) => {
  const { t } = useI18n()
  const profile = state.data.profile
  return (
    <MobileSheet title={t('Edit details')} open={open} onClose={onClose} testId="worker-mobile-profile-sheet">
      {profile && open ? <WorkerProfileEditor key={profile.profileRevision} profile={profile} busy={Boolean(state.busy)}
        save={action => { void state.run('profile', action, t('Profile saved.')) }} /> : null}
    </MobileSheet>
  )
}

/**
 * Document capture. `capture="environment"` asks the mobile browser to open the
 * camera directly; every target browser falls back to the normal file picker
 * when it cannot, so there is never a dead end.
 */
const DocumentUploadSheet: React.FC<{
  open: boolean
  onClose: () => void
  state: WorkerWorkspaceState
  platform: BrowserPlatform
  onUploaded: (id: string) => void
}> = ({ open, onClose, state, onUploaded }) => {
  const { t } = useI18n()
  return <MobileSheet title={t('Add a document')} open={open} onClose={onClose} testId="worker-mobile-document-sheet"><WorkerDocumentUpload profile={state.data.profile} onUpdated={state.load} onUploaded={onUploaded} /></MobileSheet>
}
